import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SplashScreen } from '@/components/layout/SplashScreen';
import { configurePlatformApi } from '@/lib/platformApi';
import { usePlatformAuthStore } from '@/stores/platformAuthStore';
import { fetchPlatformMe } from '../api';
import { platformKeys } from '../queries';

/**
 * Wires the platform HTTP client and resolves the operator session.
 *
 * Mounted at the /platform route rather than at the app root, so a cashier who
 * never opens the console pays nothing for it — no extra request, no extra
 * interceptor, and no platform token in memory.
 */
export function PlatformProvider({ children }: { children: ReactNode }) {
  const [configured, setConfigured] = useState(false);

  const token = usePlatformAuthStore((s) => s.token);
  const admin = usePlatformAuthStore((s) => s.admin);
  const setAdmin = usePlatformAuthStore((s) => s.setAdmin);
  const clear = usePlatformAuthStore((s) => s.clear);

  useEffect(() => {
    configurePlatformApi({
      getToken: () => usePlatformAuthStore.getState().token,

      // Clears the PLATFORM session only. A tenant session in another tab is
      // a different principal and must survive.
      onUnauthenticated: () => usePlatformAuthStore.getState().clear(),
    });

    setConfigured(true);
  }, []);

  const { data, isError } = useQuery({
    queryKey: [...platformKeys.all, 'me'],
    queryFn: fetchPlatformMe,
    enabled: configured && token !== null,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (data) setAdmin(data);
  }, [data, setAdmin]);

  useEffect(() => {
    // A token that no longer resolves is a token worth discarding — the admin
    // may have been deactivated since it was issued.
    if (isError) clear();
  }, [isError, clear]);

  if (!configured) return <SplashScreen />;

  /*
   * Wait for the admin to be IN THE STORE, not merely fetched.
   *
   * setAdmin runs in an effect, so there is one render between the query
   * resolving and the store holding it. Gating on isLoading alone would let
   * the guard below run with admin === null and bounce to the login screen —
   * the same bug the tenant side had, and the reason refreshing any page threw
   * you back to the dashboard.
   */
  if (token !== null && admin === null && !isError) return <SplashScreen />;

  return <>{children}</>;
}

/** Requires a resolved operator session. */
export function PlatformRoute() {
  const token = usePlatformAuthStore((s) => s.token);
  const admin = usePlatformAuthStore((s) => s.admin);
  const location = useLocation();

  if (token === null || admin === null) {
    const next = encodeURIComponent(location.pathname + location.search);

    return <Navigate to={`/platform/login?next=${next}`} replace />;
  }

  return <Outlet />;
}

/** The login screen, pointless once signed in. */
export function PlatformPublicRoute() {
  const token = usePlatformAuthStore((s) => s.token);
  const admin = usePlatformAuthStore((s) => s.admin);
  const location = useLocation();

  if (token !== null && admin !== null) {
    const raw = new URLSearchParams(location.search).get('next');
    const next = safePlatformNext(raw);

    return <Navigate to={next ?? '/platform'} replace />;
  }

  return <Outlet />;
}

/**
 * `next` arrives from the URL bar, so it is attacker-controlled.
 *
 * Confined to /platform specifically — not just "starts with a slash". A
 * console login that can be made to land inside the tenant app is a way to
 * walk an operator somewhere they did not intend to go.
 */
function safePlatformNext(raw: string | null): string | null {
  if (raw === null) return null;

  let path: string;

  try {
    path = decodeURIComponent(raw);
  } catch {
    return null;
  }

  if (!path.startsWith('/platform') || path.startsWith('//')) return null;
  if (path.startsWith('/platform/login')) return null;

  return path;
}
